import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  MenuItem,
  Typography,
  Grid,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Autocomplete,
  IconButton,
  Menu,
  Paper,
  Stack,
  Divider,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import CantidadEscaneadaDialog from './CantidadEscaneadaDialog';

import { getProductosPaged } from "../../../services/modules/Productos";
import { insertarVenta, GenerarFactura, getFacturacionStatus } from "../../../services/modules/Ventas";
import { getClientes } from "../../../services/modules/Clientes";
import { getRafagaConfig } from "../../../services/modules/Rafaga";

import logoPerfect from "../../../assets/Logo.png";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { BRANDING } from "../../../config/branding";
import { generarPDFFactura } from "../../../utils/facturaPdf";

// ---- Helpers ----
const toArray = (x) =>
  Array.isArray(x) ? x
    : Array.isArray(x?.data) ? x.data
      : Array.isArray(x?.items) ? x.items
        : [];

const currencyGTQ = new Intl.NumberFormat("es-GT", {
  style: "currency",
  currency: "GTQ",
  minimumFractionDigits: 2
});

const sumReservado = (detalleVenta, productId) =>
  detalleVenta.reduce((acc, item) => acc + (item.id_producto === productId ? item.cantidad : 0), 0);

const stockDisponible = (producto, detalleVenta) => {
  if (!producto) return 0;
  const stockTotal = Number(producto.stock ?? producto.stock_actual ?? producto.cantidad_inicial) || 0;
  const productId = producto.id ?? producto.id_producto ?? producto.producto_id;
  const reservado = sumReservado(detalleVenta, productId);
  return Math.max(0, stockTotal - reservado);
};

let globalPdfPreviewHandler = null;
const setGlobalPdfPreviewHandler = (handler) => {
  globalPdfPreviewHandler = handler;
};

// ---- PDF ----
const generarPDFVentaPerfect = (res) => {
  const { cliente, detalles, id_venta } = res;
  const total = detalles.reduce((sum, item) => sum + item.total, 0);

  const [año, mes, dia] = res.fechaVenta.split("T")[0].split("-");
  const fecha = `${dia}/${mes}/${año}`;
  const esCotizacion = id_venta === "COTIZACIÓN";

  const doc = new jsPDF();
  const img = new Image();
  img.src = logoPerfect;

  img.onload = () => {
    doc.addImage(img, "png", 10, 10, 60, 38);
    doc.setFontSize(18);
    doc.text(esCotizacion ? "COTIZACIÓN" : "COMPROBANTE", 105, 30, { align: "center" });

    doc.setFontSize(12);
    const companyX = 15;
    const companyY = 50;
    const addressLines = res.negocio?.direccion ? doc.splitTextToSize(res.negocio.direccion, 110).slice(0, 2) : BRANDING.pdf.addressLines;
    doc.text(res.negocio?.nombre || BRANDING.pdf.name, companyX, companyY);
    addressLines.forEach((line, index) => {
      doc.text(line, companyX, companyY + 6 * (index + 1));
    });
    doc.text(
      `Teléfono: ${res.negocio?.telefono || BRANDING.phoneText}`,
      companyX,
      companyY + 6 * (addressLines.length + 1)
    );

    doc.text(`Cliente: ${cliente?.nombre || "CF"}`, 140, 50);
    doc.text(`NIT: ${cliente?.nit || "CF"}`, 140, 56);
    doc.text(`Fecha: ${fecha}`, 140, 62);
    doc.text(`${esCotizacion ? "Cotización" : "Factura"} ID: ${id_venta}`, 140, 68);

    autoTable(doc, {
      startY: 75,
      head: [["Cantidad", "Descripción", "Precio Unitario", "Total"]],
      body: detalles.map((item) => [
        item.cantidad,
        item.descripcion,
        `Q${item.precio_unitario.toFixed(2)}`,
        `Q${item.total.toFixed(2)}`
      ]),
      styles: { halign: "center", lineWidth: 0.3, lineColor: [0, 0, 0], cellPadding: 3, textColor: 20 },
      headStyles: { fillColor: [0, 0, 0], textColor: [255, 255, 255], halign: "center" },
      theme: "grid"
    });

    doc.setFontSize(14);
    const posY = doc.lastAutoTable.finalY + 10;
    doc.text(`Total a pagar: Q${total.toFixed(2)}`, 150, posY);

    if (esCotizacion) {
      doc.setFontSize(11);
      doc.setTextColor(100);
      doc.text("Esta cotización es válida por 10 días calendario a partir de la fecha de emisión.", 15, posY + 15);
      doc.text("Sujeto a disponibilidad de productos y cambios de precio sin previo aviso.", 15, posY + 21);
    }

    const previewHandler =
      typeof res?.onPreview === "function" ? res.onPreview : globalPdfPreviewHandler;

    if (typeof previewHandler === "function") {
      const filename = `${esCotizacion ? "Cotizacion" : "Factura"}_${id_venta}.pdf`;
      const blob = doc.output("blob");
      const url = URL.createObjectURL(blob);
      previewHandler({ url, filename });
      return;
    }

    doc.save(`${esCotizacion ? "Cotización" : "Factura"}_${id_venta}.pdf`);
  };
};

const Ventas = () => {
  const [negocio, setNegocio] = useState(null);
  useEffect(() => {
    let active = true;
    getRafagaConfig().then((data) => { if (active) setNegocio(data.negocio); }).catch(() => {});
    return () => { active = false; };
  }, []);
  // Autocomplete (server-side con infinite scroll)
  const [productos, setProductos] = useState([]);
  const [loadingOpciones, setLoadingOpciones] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [openAuto, setOpenAuto] = useState(false);

  const [query, setQuery] = useState("");      // input del usuario
  const [page, setPage] = useState(0);         // página actual cargada
  const [hasMore, setHasMore] = useState(false);
  const reqSeq = useRef(0);
  const debounceId = useRef(null);
  const pdfPreviewHandlerRef = useRef(null);

  // Otros estados
  const [clientes, setClientes] = useState([]);
  const [clienteInput, setClienteInput] = useState("");
  const [detalleVenta, setDetalleVenta] = useState([]);
  const detalleActual = useRef(detalleVenta);
  detalleActual.current = detalleVenta;
  const lecturaEnCurso = useRef(false);
  const productoInputRef = useRef(null);
  const [cantidadEscaneada, setCantidadEscaneada] = useState(null);
  const [productoSeleccionado, setProductoSeleccionado] = useState(null);

  // Cantidad como TEXTO para permitir vacío y evitar "0" por defecto
  const [cantidad, setCantidad] = useState("");
  const cantidadNum = parseInt(cantidad, 10);
  const isCantidadValida = Number.isFinite(cantidadNum) && cantidadNum > 0;

  const [idCliente, setIdCliente] = useState("");
  const [tipoVenta, setTipoVenta] = useState("Contado");
  const [moneda] = useState(1); // 👈 oculta en UI, pero se envía
  const [precioSeleccionado, setPrecioSeleccionado] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  // Responsive
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState("");
  const [pdfPreviewFilename, setPdfPreviewFilename] = useState("");
  const [felDialogOpen, setFelDialogOpen] = useState(false);
  const [felRetryDialogOpen, setFelRetryDialogOpen] = useState(false);
  const [felRetryState, setFelRetryState] = useState(null); // { ventaRes, facturar, cliente, detalles, fechaVenta, errorText }
  const [savingVenta, setSavingVenta] = useState(false);
  const [logoImgEl, setLogoImgEl] = useState(null);
  const [felEnabled, setFelEnabled] = useState(Boolean(BRANDING?.felEnabled));
  const [generarFelChecked, setGenerarFelChecked] = useState(true);

  const openPdfPreview = ({ url, filename }) => {
    if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
    setPdfPreviewUrl(url);
    setPdfPreviewFilename(filename);
    setPdfPreviewOpen(true);
  };

  pdfPreviewHandlerRef.current = openPdfPreview;

  useEffect(() => {
    setGlobalPdfPreviewHandler((payload) => pdfPreviewHandlerRef.current?.(payload));
    return () => setGlobalPdfPreviewHandler(null);
  }, []);

  const closePdfPreview = () => {
    setPdfPreviewOpen(false);
    if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
    setPdfPreviewUrl("");
    setPdfPreviewFilename("");
  };

  useEffect(() => () => {
    if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
  }, [pdfPreviewUrl]);

  // Config
  const MIN_CHARS = 1;
  const PAGE_SIZE = 50;
  const debounceMs = 300;

  const obtenerFechaLocal = () => {
    const ahora = new Date();
    const offsetMs = ahora.getTimezoneOffset() * 60000;
    const localTime = new Date(ahora.getTime() - offsetMs);
    return localTime.toISOString().slice(0, 16); // yyyy-MM-ddTHH:mm
  };
  const [fechaVenta, setFechaVenta] = useState(obtenerFechaLocal());

  // Cargar clientes una vez
  useEffect(() => {
    (async () => {
      try {
        const clientesData = await getClientes();
        setClientes(toArray(clientesData));
      } catch (e) {
        console.error("Error al cargar clientes", e);
        setClientes([]);
      }
    })();
  }, []);

  useEffect(() => {
    const img = new Image();
    img.onload = () => setLogoImgEl(img);
    img.src = logoPerfect;
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const status = await getFacturacionStatus();
        if (!mounted) return;
        setFelEnabled(Boolean(BRANDING?.felEnabled) && Boolean(status?.enabled));
      } catch (_e) {
        if (!mounted) return;
        setFelEnabled(Boolean(BRANDING?.felEnabled));
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  // --- Buscar página (genérica) ---
  const fetchPage = async ({ q, pageIndex }) => {
    const mySeq = ++reqSeq.current;

    if (pageIndex === 0) {
      setLoadingOpciones(true);
      setLoadingMore(false);
    } else {
      setLoadingMore(true);
    }

    try {
      const data = await getProductosPaged({ q, page: pageIndex, pageSize: PAGE_SIZE });
      const items = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
      const total = Number.isFinite(data?.total) ? data.total : undefined;

      // Evitar carreras
      if (mySeq !== reqSeq.current) return;

      if (pageIndex === 0) {
        setProductos(items);
      } else {
        // merge sin duplicados por id
        setProductos(prev => {
          const seen = new Set(prev.map(p => p.id));
          const nuevos = items.filter(p => !seen.has(p.id));
          return [...prev, ...nuevos];
        });
      }

      setPage(pageIndex);

      // hasMore: si viene total, úsalo; si no, infiere por tamaño de página
      if (typeof total === "number") {
        setHasMore((pageIndex + 1) * PAGE_SIZE < total);
      } else {
        setHasMore(items.length === PAGE_SIZE);
      }
    } catch (err) {
      console.error("Error buscando productos:", err);
      if (pageIndex === 0) setProductos([]);
      setHasMore(false);
    } finally {
      if (mySeq === reqSeq.current) {
        setLoadingOpciones(false);
        setLoadingMore(false);
      }
    }
  };

  // Debounce del texto
  useEffect(() => {
    if (debounceId.current) clearTimeout(debounceId.current);
    const q = (query || "").trim();
    if (q.length < MIN_CHARS) {
      setProductos([]);
      setHasMore(false);
      setPage(0);
      setLoadingOpciones(false);
      setLoadingMore(false);
      return;
    }
    debounceId.current = setTimeout(() => {
      // nueva búsqueda: page 0
      fetchPage({ q, pageIndex: 0 });
    }, debounceMs);
    return () => clearTimeout(debounceId.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  // Scroll del listbox para cargar más
  const handleListboxScroll = (event) => {
    const listboxNode = event.currentTarget;
    const nearBottom = listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 64;
    if (nearBottom && hasMore && !loadingMore && !loadingOpciones) {
      const q = (query || "").trim();
      if (q.length >= MIN_CHARS) {
        fetchPage({ q, pageIndex: page + 1 });
      }
    }
  };

  const handleProductoInputKeyDown = async (event) => {
    if (event.key !== "Enter") return;
    const codigoIngresado = (query || "").trim();
    if (!codigoIngresado) return;
    event.preventDefault();
    event.defaultMuiPrevented = true;
    event.stopPropagation();
    if (lecturaEnCurso.current || cantidadEscaneada) return;
    lecturaEnCurso.current = true;
    try {

    const normalizar = (texto) => (texto || "").toString().trim().toLowerCase();
    const lista = Array.isArray(productos) ? productos : [];
    let productoCoincidente = lista.find(
      (item) => [item?.codigo,item?.codigo_barras,item?.codigo_etiqueta].some(value => normalizar(value) === normalizar(codigoIngresado))
    );

    if (!productoCoincidente) {
      try {
        const data = await getProductosPaged({ q: codigoIngresado, page: 0, pageSize: 1 });
        const items = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
        productoCoincidente = items.find(item => [item?.codigo,item?.codigo_barras,item?.codigo_etiqueta].some(value => normalizar(value) === normalizar(codigoIngresado)));
      } catch (error) {
        console.error("Error buscando producto por codigo", error);
      }
    }

    if (!productoCoincidente) {
      alert("Producto no encontrado para el codigo ingresado.");
      return;
    }

    const productId = productoCoincidente.id ?? productoCoincidente.id_producto ?? productoCoincidente.producto_id;
    const existente = detalleActual.current.findIndex(item => item.id_producto === productId);
    setOpenAuto(false);
    if (existente >= 0) {
      const linea = detalleActual.current[existente];
      setQuery('');
      setCantidadEscaneada({ producto: productoCoincidente, index: existente,
        inicial: linea.cantidad + 1,
        disponible: stockDisponible(productoCoincidente, detalleActual.current) + linea.cantidad });
    } else {
      agregarDetalle(productoCoincidente, 1);
    }
    } finally { lecturaEnCurso.current = false; }
  };

  const cerrarCantidadEscaneada = () => {
    setCantidadEscaneada(null);
    setProductoSeleccionado(null); setQuery(''); setCantidad(''); setPrecioSeleccionado(null);
    setOpenAuto(false);
    setTimeout(() => productoInputRef.current?.focus(), 0);
  };
  const confirmarCantidadEscaneada = total => {
    const { index, producto } = cantidadEscaneada;
    const actual = detalleActual.current;
    const linea = actual[index];
    const disponible = stockDisponible(producto, actual) + (linea?.cantidad || 0);
    if (!linea || !Number.isSafeInteger(total) || total <= 0 || total > disponible) return;
    const updated = actual.map((item, i) => i === index
      ? { ...item, cantidad: total, total: total * item.precio_unitario } : item);
    detalleActual.current = updated;
    setDetalleVenta(updated);
    cerrarCantidadEscaneada();
  };

  // Reset total
  const resetFormulario = () => {
    setDetalleVenta([]);
    setCantidadEscaneada(null);
    setProductoSeleccionado(null);
    setCantidad(""); // 👈 ahora vacío
    setPrecioSeleccionado(null);
    setAnchorEl(null);
    setIdCliente("");
    setTipoVenta("Contado");
    setFechaVenta(obtenerFechaLocal());
    setOpenAuto(false);
    setQuery("");
    setProductos([]);
    setHasMore(false);
    setPage(0);
  };

  const handleOpenPrecioMenu = (e) => setAnchorEl(e.currentTarget);
  const handleClosePrecioMenu = () => setAnchorEl(null);
  const handleSeleccionarPrecio = (precio) => { setPrecioSeleccionado(precio); handleClosePrecioMenu(); };

  const agregarDetalle = (productoForzado, cantidadForzada) => {
    const isReactEvent =
      productoForzado &&
      typeof productoForzado === "object" &&
      ("nativeEvent" in productoForzado || "currentTarget" in productoForzado || "target" in productoForzado);

    const producto =
      !isReactEvent && productoForzado && typeof productoForzado === "object"
        ? productoForzado
        : productoSeleccionado;
    if (!producto || typeof producto !== "object") return;

    const cantidadEvaluada = Number.isFinite(cantidadForzada)
      ? cantidadForzada
      : Number.parseInt(cantidad, 10);

    if (!Number.isFinite(cantidadEvaluada) || cantidadEvaluada <= 0) {
      alert("Ingresa una cantidad valida (> 0).");
      return;
    }

    const disponible = stockDisponible(producto, detalleActual.current);
    if (cantidadEvaluada > disponible) {
      alert(`No hay stock suficiente. Disponible: ${disponible}`);
      return;
    }

    const usarPrecioManual = producto === productoSeleccionado && Number.isFinite(precioSeleccionado);
    const precioBase = usarPrecioManual
      ? precioSeleccionado
      : parseFloat(producto.precio_venta);
    const precioUnitario = Number.isFinite(precioBase) ? precioBase : 0;

    const updated = [
      ...detalleActual.current,
      {
        id_producto: producto.id ?? producto.id_producto ?? producto.producto_id,
        codigo: producto.codigo ?? producto.id ?? producto.id_producto ?? "",
        descripcion: producto.descripcion,
        precio_unitario: precioUnitario,
        cantidad: cantidadEvaluada,
        total: cantidadEvaluada * precioUnitario
      }
    ];
    detalleActual.current = updated;
    setDetalleVenta(updated);

    // limpiar entrada
    setProductoSeleccionado(null);
    setQuery("");
    setCantidad("");
    setPrecioSeleccionado(null);
    setProductos([]);
    setHasMore(false);
    setPage(0);
  };

  const eliminarDetalle = (index) => {
    const copia = [...detalleVenta];
    copia.splice(index, 1);
    setDetalleVenta(copia);
  };

  const calcularTotal = () => detalleVenta.reduce((acc, item) => acc + item.total, 0);

  const guardarVenta = async (generarFel) => {
    try {
      if (savingVenta) return;
      if (!idCliente) return alert("Debe seleccionar un cliente.");
      if (detalleVenta.length === 0) return alert("Debe agregar al menos un producto a la venta.");
      setSavingVenta(true);

      const venta = {
        id_cliente: idCliente,
        tipo_venta: tipoVenta,
        id_moneda: moneda, // 👈 se envía aunque no se muestre
        fecha_venta: fechaVenta,
        detalles: detalleVenta
      };

      const cliente = clientes.find(c => c.id_cliente === idCliente);

      if (tipoVenta === "Cotización") {
        generarPDFVentaPerfect({ negocio, cliente, detalles: detalleVenta, fechaVenta, id_venta: "COTIZACIÓN" });
        alert("Cotización generada correctamente");
        resetFormulario();
        return;
      }

      const res = await insertarVenta(venta);
      const ventaRes = res;
      alert(ventaRes.message);

      if (generarFel) {
        const itemsFactura = detalleVenta.map((item) => ({
          descripcion: item.descripcion,
          bienOServicio: item.tipo || "B",
          cantidad: item.cantidad,
          precioUnitario: item.precio_unitario,
          precioDescuento: 0,
        }));

        const facturar = {
          nitReceptor: cliente?.nit || "CF",
          nombreReceptor: cliente?.nombre || "CONSUMIDOR FINAL",
          direccionReceptor: cliente?.direccion || "CIUDAD",
          items: itemsFactura,
          id_venta: ventaRes.id_venta,
          fechaHoraEmision: fechaVenta,
        };

        const resFactura = await GenerarFactura(facturar);
        if (resFactura?.exitoso) {
          await generarPDFFactura(
            { ...ventaRes, negocio, cliente, detalles: detalleVenta, fechaVenta },
            resFactura,
            logoImgEl,
            { save: false, onPreview: openPdfPreview }
          );
        } else {
          console.error("Error al certificar FEL", resFactura);
          const errorText = JSON.stringify(resFactura?.error || resFactura);
          alert(errorText);
          // Guardar contexto para reintentar FEL SIN volver a crear la venta (evita bajar stock dos veces).
          setFelRetryState({
            ventaRes: ventaRes,
            facturar,
            cliente,
            detalles: detalleVenta,
            fechaVenta,
            errorText,
          });
          setFelRetryDialogOpen(true);
        }
      } else {
        generarPDFVentaPerfect({ ...ventaRes, negocio, cliente, detalles: detalleVenta, fechaVenta });
      }

      // Nueva venta
      resetFormulario();
    } catch (error) {
      console.error("Error al guardar venta", error);
      alert("Error al guardar la venta");
    } finally {
      setSavingVenta(false);
    }
  };

  const solicitarGuardarVenta = () => {
    if (!idCliente) return alert("Debe seleccionar un cliente.");
    if (detalleVenta.length === 0) return alert("Debe agregar al menos un producto a la venta.");
    if (tipoVenta === "Cotizacion" || tipoVenta === "CotizaciÃ³n") return guardarVenta(false);

    // Al abrir el diÃ¡logo, por defecto FEL marcado si estÃ¡ disponible.
    setGenerarFelChecked(Boolean(felEnabled));
    setFelDialogOpen(true);
  };

  const retryFel = async () => {
    if (!felRetryState?.facturar) return;
    try {
      const resFactura = await GenerarFactura(felRetryState.facturar);
      if (resFactura?.exitoso) {
        await generarPDFFactura(
          { ...felRetryState.ventaRes, negocio, cliente: felRetryState.cliente, detalles: felRetryState.detalles, fechaVenta: felRetryState.fechaVenta },
          resFactura,
          logoImgEl,
          { save: false, onPreview: openPdfPreview }
        );
        setFelRetryDialogOpen(false);
        setFelRetryState(null);
        return;
      }

      const errorText = JSON.stringify(resFactura?.error || resFactura);
      setFelRetryState((prev) => (prev ? { ...prev, errorText } : prev));
      alert(errorText);
    } catch (e) {
      console.error("Error reintentando FEL", e);
      alert("Error reintentando FEL");
    }
  };

  const disponibleActual = productoSeleccionado ? stockDisponible(productoSeleccionado, detalleVenta) : null;

  return (
    <Box p={{ xs: 2, md: 3 }}>
      <Typography variant="h5" gutterBottom>
        Registrar Venta
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            options={clientes}
            value={clientes.find((cli) => String(cli.id_cliente) === String(idCliente)) || null}
            onChange={(_, newValue) => setIdCliente(newValue?.id_cliente || "")}
            inputValue={clienteInput}
            onInputChange={(_, value) => setClienteInput(value)}
            isOptionEqualToValue={(option, value) =>
              String(option.id_cliente) === String(value?.id_cliente)
            }
            getOptionLabel={(option) =>
              option ? `${option.nombre || "Sin nombre"} • NIT ${option.nit || "CF"}` : ""
            }
            filterOptions={(options, { inputValue }) => {
              const q = inputValue.trim().toLowerCase();
              if (q.length < 2) return [];
              return options.filter((opt) =>
                [opt.nombre, opt.nit]
                  .filter(Boolean)
                  .some((field) => field.toLowerCase().includes(q))
              );
            }}
            renderOption={(props, option) => (
              <li {...props} key={option.id_cliente}>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography variant="body2" fontWeight={600}>
                    {option.nombre || "Sin nombre"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    NIT: {option.nit || "CF"}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Cliente"
                placeholder="Buscar por nombre o NIT"
                fullWidth
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <TextField
            label="Tipo de Venta"
            select
            value={tipoVenta}
            onChange={(e) => setTipoVenta(e.target.value)}
            fullWidth
          >
            <MenuItem value="Contado">Contado</MenuItem>
            <MenuItem value="Crédito">Crédito</MenuItem>
            <MenuItem value="Cotización">Cotización</MenuItem>
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label="Fecha de Venta"
            type="datetime-local"
            value={fechaVenta}
            onChange={(e) => setFechaVenta(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Grid>

        <Grid item xs={12}>
          <Box display="flex" gap={2} alignItems="flex-start" flexWrap={{ xs: "wrap", sm: "nowrap" }}>
            <TextField
              label="Cantidad"
              value={cantidad}
              onChange={(e) => {
                // Solo dígitos, permite vacío
                const soloDigitos = e.target.value.replace(/[^\d]/g, "");
                setCantidad(soloDigitos);
              }}
              inputProps={{ inputMode: "numeric", pattern: "\\d*" }}
              sx={{ width: { xs: "100%", sm: "20%" } }}
              error={
                !!productoSeleccionado &&
                isCantidadValida &&
                cantidadNum > (disponibleActual ?? 0)
              }
              helperText={productoSeleccionado ? `Disponible: ${disponibleActual}` : ""}
            />

            <Box flexGrow={1} sx={{ width: { xs: "100%", sm: "auto" } }}>
              <Autocomplete
                open={openAuto && query.trim().length >= MIN_CHARS}
                onOpen={() => setOpenAuto(true)}
                onClose={() => setOpenAuto(false)}

                // opciones vienen del servidor; no filtramos en cliente
                options={Array.isArray(productos) ? productos : []}
                filterOptions={(opts) => opts}

                loading={loadingOpciones || loadingMore}
                loadingText={loadingMore ? "Cargando más…" : "Buscando…"}
                noOptionsText={query.trim().length < MIN_CHARS ? "Escribe para buscar…" : "Sin coincidencias"}

                getOptionLabel={(option) => {
                  if (!option || typeof option !== "object") return "";
                  const cod = option.codigo ? `[${option.codigo}] ` : "";
                  const modelo = option.modelo || "";
                  const cat = option.categoria || "";
                  const desc = option.descripcion || "";
                  const p1 = Number.parseFloat(option.precio_venta || 0);
                  const precioStr = Number.isFinite(p1) ? ` — ${currencyGTQ.format(p1)}` : "";
                  return [cod + modelo, cat, desc].filter(Boolean).join(" - ") + precioStr;
                }}
                isOptionEqualToValue={(opt, val) => (opt?.id ?? opt) === (val?.id ?? val)}

                value={productoSeleccionado}
                onChange={(_, newValue) => {
                  setProductoSeleccionado(newValue);
                  if (newValue && Number.isFinite(parseFloat(newValue.precio_venta))) {
                    setPrecioSeleccionado(parseFloat(newValue.precio_venta));
                  } else {
                    setPrecioSeleccionado(null);
                  }
                }}

                inputValue={query}
                onInputChange={(_, newInputValue) => setQuery(newInputValue)}

                // INFINITE SCROLL: escucha scroll del listbox
                ListboxProps={{ onScroll: handleListboxScroll }}

                renderOption={(props, option) => {
                  const p1 = Number.parseFloat(option?.precio_venta || 0) || 0;
                  const p2 = +(p1 * 1.1).toFixed(2);
                  const p3 = +(p1 * 1.2).toFixed(2);
                  const disp = stockDisponible(option, detalleVenta);
                  return (
                    <li {...props} key={option.id}>
                      <Box display="flex" flexDirection="column" width="100%">
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" fontWeight={600}>
                            {option.codigo ? `[${option.codigo}] ` : ""}
                            {option.modelo || ""}
                            {option.categoria ? ` - ${option.categoria}` : ""}
                            {option.descripcion ? ` - ${option.descripcion}` : ""}
                          </Typography>
                          <Typography variant="body2">{currencyGTQ.format(p1)}</Typography>
                        </Box>
                        <Box display="flex" gap={2} mt={0.25}>
                          <Typography variant="caption">P1: {currencyGTQ.format(p1)}</Typography>
                          <Typography variant="caption">P2: {currencyGTQ.format(p2)}</Typography>
                          <Typography variant="caption">P3: {currencyGTQ.format(p3)}</Typography>
                          <Typography variant="caption" sx={{ marginLeft: "auto" }}>
                            Stock: <strong>{disp}</strong>
                          </Typography>
                        </Box>
                      </Box>
                    </li>
                  );
                }}

                renderInput={(params) => (
                  <TextField
                    {...params}
                    inputRef={productoInputRef}
                    label="Producto (escribe código, modelo, categoría o descripción)"
                    fullWidth
                    onKeyDown={handleProductoInputKeyDown}
                  />
                )}
              />

              {productoSeleccionado && typeof productoSeleccionado === "object" && (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  Stock disponible: <strong>{disponibleActual}</strong>
                </Typography>
              )}
            </Box>

            {productoSeleccionado && typeof productoSeleccionado === "object" && (
              <Box sx={{ width: { xs: "100%", sm: "auto" } }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleOpenPrecioMenu}
                  sx={{ minWidth: "40px", padding: 0, mb: 1, mr: 1 }}
                >
                  P
                </Button>

                {Number.isFinite(precioSeleccionado) && (
                  <Typography variant="body2" component="span">
                    {currencyGTQ.format(precioSeleccionado)}
                  </Typography>
                )}

                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClosePrecioMenu}>
                  <MenuItem onClick={() => handleSeleccionarPrecio(parseFloat(productoSeleccionado.precio_venta))}>
                    Precio 1: {currencyGTQ.format(parseFloat(productoSeleccionado.precio_venta))}
                  </MenuItem>
                  <MenuItem onClick={() => handleSeleccionarPrecio(parseFloat(productoSeleccionado.precio_venta) * 1.1)}>
                    Precio 2: {currencyGTQ.format(parseFloat(productoSeleccionado.precio_venta) * 1.1)}
                  </MenuItem>
                  <MenuItem onClick={() => handleSeleccionarPrecio(parseFloat(productoSeleccionado.precio_venta) * 1.2)}>
                    Precio 3: {currencyGTQ.format(parseFloat(productoSeleccionado.precio_venta) * 1.2)}
                  </MenuItem>
                </Menu>
              </Box>
            )}

            <Button
              variant="contained"
              onClick={() => agregarDetalle()}
              disabled={
                !productoSeleccionado ||
                !isCantidadValida ||
                (productoSeleccionado && cantidadNum > (disponibleActual ?? 0))
              }
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              Agregar
            </Button>
          </Box>
        </Grid>
      </Grid>

      <Box mt={4}>
        <Typography variant="h6" gutterBottom>Detalle de Venta</Typography>

        {/* Vista móvil: tarjetas */}
        {isXs ? (
          <Box>
            {detalleVenta.length === 0 && (
              <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 0 }}>
                <Typography align="center" color="text.secondary">Sin ítems</Typography>
              </Paper>
            )}

            {detalleVenta.map((item, index) => (
              <Paper key={index} sx={{ p: 2, mb: 1.5, borderRadius: 2, boxShadow: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Box sx={{ fontWeight: 700 }}>{item.descripcion}</Box>
                    <Box sx={{ fontSize: 12, color: "text.secondary" }}>
                      Cant: {item.cantidad}
                    </Box>
                  </Box>
                  <IconButton onClick={() => eliminarDetalle(index)} color="error">
                    <DeleteIcon />
                  </IconButton>
                </Stack>

                <Divider sx={{ my: 1 }} />

                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Precio unitario</Typography>
                    <TextField
                      type="number"
                      size="small"
                      value={item.precio_unitario}
                      onChange={(e) => {
                        const updated = [...detalleVenta];
                        const nuevoPrecio = parseFloat(e.target.value || "0");
                        updated[index].precio_unitario = Number.isNaN(nuevoPrecio) ? 0 : nuevoPrecio;
                        updated[index].total = updated[index].precio_unitario * updated[index].cantidad;
                        setDetalleVenta(updated);
                      }}
                      InputProps={{ inputProps: { min: 0, step: "0.01" } }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Total</Typography>
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="subtitle2">
                        {currencyGTQ.format(item.total)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            ))}

            {detalleVenta.length > 0 && (
              <Paper sx={{ p: 2, borderRadius: 2, mt: 2 }}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="subtitle2">Total:</Typography>
                  <Typography variant="subtitle2" color="error" fontWeight={700}>
                    {currencyGTQ.format(calcularTotal())}
                  </Typography>
                </Stack>
              </Paper>
            )}
          </Box>
        ) : (
          // Desktop: tabla con cabecera negra y bordes
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Cantidad</TableCell>
                  <TableCell>Descripción</TableCell>
                  <TableCell>Precio Unitario</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell>Acción</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {detalleVenta.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{item.cantidad}</TableCell>
                    <TableCell>{item.descripcion}</TableCell>
                    <TableCell>
                    <TextField
                      type="number"
                      size="small"
                      value={item.precio_unitario}
                      onChange={(e) => {
                        const updated = [...detalleVenta];
                        const nuevoPrecio = parseFloat(e.target.value || "0");
                        updated[index].precio_unitario = Number.isNaN(nuevoPrecio) ? 0 : nuevoPrecio;
                        updated[index].total = updated[index].precio_unitario * updated[index].cantidad;
                        setDetalleVenta(updated);
                      }}
                      InputProps={{ inputProps: { min: 0, step: "0.01" } }}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    {currencyGTQ.format(item.total)}
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => eliminarDetalle(index)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {detalleVenta.length > 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="right">
                    <strong>Total:</strong>
                  </TableCell>
                  <TableCell colSpan={2}>
                    <Typography variant="h4" color="error" fontWeight="bold">
                      {currencyGTQ.format(calcularTotal())}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Sin ítems
                  </TableCell>
                </TableRow>
              )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <Box mt={4}>
        <Button variant="contained" color="success" onClick={solicitarGuardarVenta} disabled={savingVenta}>
          {savingVenta ? "Procesando..." : "Guardar Venta"}
        </Button>
      </Box>

      {cantidadEscaneada && <CantidadEscaneadaDialog
        {...cantidadEscaneada}
        onCancel={cerrarCantidadEscaneada}
        onConfirm={confirmarCantidadEscaneada}
      />}
      <Dialog open={felDialogOpen} onClose={() => setFelDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmar venta</DialogTitle>
        <DialogContent sx={{ mt: 1 }}>
          <Typography variant="body2">Desea Generar la Venta</Typography>
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Checkbox
                checked={Boolean(generarFelChecked) && Boolean(felEnabled)}
                onChange={(e) => setGenerarFelChecked(e.target.checked)}
                disabled={!felEnabled}
              />
            }
            label="Generar FEL"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFelDialogOpen(false)} disabled={savingVenta}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setFelDialogOpen(false);
              guardarVenta(Boolean(generarFelChecked) && Boolean(felEnabled));
            }}
            disabled={savingVenta}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pdfPreviewOpen} onClose={closePdfPreview} fullWidth maxWidth="md">
        <DialogTitle>Vista previa</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {pdfPreviewUrl ? (
            <iframe
              title="Vista previa PDF"
              src={pdfPreviewUrl}
              style={{ width: "100%", height: "75vh", border: 0 }}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            component="a"
            href={pdfPreviewUrl || undefined}
            download={pdfPreviewFilename || undefined}
            variant="contained"
            disabled={!pdfPreviewUrl}
          >
            Descargar PDF
          </Button>
          <Button onClick={closePdfPreview}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={felRetryDialogOpen} onClose={() => setFelRetryDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>FEL falló</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" gutterBottom>
            La venta ya fue registrada (ID: {felRetryState?.ventaRes?.id_venta}).
            Puedes reintentar la certificación FEL sin volver a guardar la venta.
          </Typography>
          {felRetryState?.errorText ? (
            <TextField
              label="Detalle del error"
              value={felRetryState.errorText}
              fullWidth
              multiline
              minRows={4}
              InputProps={{ readOnly: true }}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (!felRetryState) return;
              generarPDFVentaPerfect({
                negocio,
                ...felRetryState.ventaRes,
                cliente: felRetryState.cliente,
                detalles: felRetryState.detalles,
                fechaVenta: felRetryState.fechaVenta,
              });
            }}
          >
            Imprimir sin FEL
          </Button>
          <Button onClick={() => setFelRetryDialogOpen(false)}>Cerrar</Button>
          <Button variant="contained" onClick={retryFel} disabled={!felRetryState?.facturar}>
            Reintentar FEL
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Ventas;
